/**
 * DowngradePopup — confirmation modal when user selects a plan below current.
 *
 * Figma source: `343:4181` (Профиль — понизить тариф; modal instance `557:10595`).
 *
 * Layout (verbatim from Figma plugin export):
 *   bottom-sheet, max-width 370, rounded-24,
 *   linear-gradient 180° #5E4112 0% → #121214 50% → #121214 100%,
 *   padding 24/48/24/24 (top extra to host decorative arrow).
 *
 * Decorative gold downward-arrow icon — `/icons/downgrade-arrow.webp`,
 * 64×64, centered, sits −24px above the modal top edge.
 *
 * Header: emoji tier prefix («👑 Premium» / «💎 Ultra»),
 *         then «{fromLabel} → {toLabel}», Raleway Bold 16, center.
 * Question: «Вы хотите понизить тарифный план?», Raleway Bold 16, center.
 * Detail rows: «Текущий план активен ещё: N дней» + «Дата смены плана: …»,
 *         Raleway Medium 12, grey label + white value, center.
 * Actions: HORIZONTAL row, two equal-width buttons:
 *   «Да, понизить» — glass (UIButton secondary)
 *   «Отменить»     — white  (UIButton tertiary)
 * Close × — top-right of modal, 20×20.
 *
 * Props:
 *   fromPlan    ('ULTRA'|'PREMIUM'|'FREE') — current plan
 *   toPlan      ('FREE'|'PREMIUM'|'ULTRA') — target (lower) plan
 *   daysLeft    (number|null) — days remaining in current period
 *   changeDate  (string|null) — ISO date when switch happens
 *   loading     (boolean)
 *   onConfirm   (fn) — «Да, понизить»
 *   onCancel    (fn) — «Отменить» / × / overlay
 */

import { useTranslation } from 'react-i18next';
import { UIButton } from '../primitives';
import { formatAppDate } from '../../utils/dateFormat';

const PLAN_DISPLAY = {
  FREE: 'Free',
  PREMIUM: 'Premium',
  ULTRA: 'Ultra',
};

const PLAN_EMOJI = {
  PREMIUM: '\u{1F451}',
  ULTRA: '\u{1F48E}',
};

function formatChangeDate(isoString) {
  return formatAppDate(isoString) || null;
}

function pluralizeDays(n, t) {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return t('downgrade.days5');
  if (mod10 === 1) return t('downgrade.day1');
  if (mod10 >= 2 && mod10 <= 4) return t('downgrade.days234');
  return t('downgrade.days5');
}

export default function DowngradePopup({
  fromPlan,
  toPlan,
  daysLeft = null,
  changeDate = null,
  loading = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const fromKey = (fromPlan || '').toString().toUpperCase();
  const toKey = (toPlan || '').toString().toUpperCase();
  const fromLabel = PLAN_DISPLAY[fromKey] ?? fromPlan;
  const toLabel = PLAN_DISPLAY[toKey] ?? toPlan;
  const tierEmoji = PLAN_EMOJI[fromKey] || '';
  const dateLabel = formatChangeDate(changeDate);

  return (
    /* Backdrop — black 64% */
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/[0.64]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="downgrade-title"
      onClick={onCancel}
    >
      {/* Modal — bottom sheet with brown→dark linear gradient */}
      <div
        className="relative mx-4 mb-6 flex w-full max-w-[370px] flex-col gap-6 rounded-home-24 px-6 pt-12 pb-6 bg-popup-gradient"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative tier-down arrow — 64×64 PNG, sits −24px above modal */}
        <img
          src="/icons/downgrade-arrow.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 left-1/2 h-16 w-16 -translate-x-1/2 select-none"
        />

        {/* Close × — top-right corner of modal */}
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          aria-label={t('common.close')}
          data-testid="downgrade-close"
          className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center text-text-primary opacity-80 hover:opacity-100 disabled:opacity-40"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
            <path
              d="M4 4 L16 16 M16 4 L4 16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Text block — title + question + detail rows */}
        <div className="flex flex-col items-center gap-3">
          <h2
            id="downgrade-title"
            className="font-sans text-center text-[16px] font-bold leading-tight text-text-primary"
          >
            {tierEmoji && <span>{tierEmoji} </span>}
            {fromLabel} <span className="text-text-tertiary">→</span> {toLabel}
          </h2>

          <p className="font-sans text-center text-[16px] font-bold leading-tight text-text-primary">
            {t('downgrade.question')}
          </p>

          {daysLeft != null && daysLeft > 0 && (
            <p className="font-sans text-center text-[12px] font-medium leading-tight">
              <span className="text-[#C9C9C9]">{t('downgrade.activeLeft')}</span>{' '}
              <span className="text-text-primary">
                {daysLeft} {pluralizeDays(daysLeft, t)}
              </span>
            </p>
          )}

          {dateLabel && (
            <p className="font-sans text-center text-[12px] font-medium leading-tight">
              <span className="text-[#C9C9C9]">{t('downgrade.changeDate')}</span>{' '}
              <span className="text-text-primary">{dateLabel}</span>
            </p>
          )}
        </div>

        {/* Actions — horizontal row, equal width */}
        <div className="flex flex-row gap-2">
          <UIButton
            type="secondary"
            size="md"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1"
            data-testid="downgrade-confirm"
          >
            {loading ? t('common.processing') : t('downgrade.confirm')}
          </UIButton>
          <UIButton
            type="tertiary"
            size="md"
            onClick={onCancel}
            disabled={loading}
            className="flex-1"
            data-testid="downgrade-cancel"
          >
            {t('common.cancel')}
          </UIButton>
        </div>
      </div>
    </div>
  );
}
